import { INSIGHT } from '../insights/insight-gate/insight-rules.config';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EMPTY, filter, map, merge, mergeMap, of, switchMap, tap } from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import {
  groupedCodesOf,
  LOCATION_UPLOAD_MESSAGES,
  locationUploadRejectedMessage,
  locationUploadSuccessMessage,
  locationFilePathFor,
  QUALIFICATION_CODE,
  Survey,
  SURVEY_ACTIVITY_LOG_MESSAGES,
  SurveyLocationUploadResult,
  supportsSurveyTags,
  uploadedLocationQualification,
  validCodeLabels,
} from '@ps/survey-utils';
import {
  UserSettingsActionsFacade,
  UserSettingsSelectorsFacade,
} from '@ps/user-settings-data-access';
import {
  SupplierActions,
  SupplierApiActions,
  SurveyActivityLogActions,
} from '@ps/survey-data-access';
import { TagsActionsFacade } from '@ps/tags-data-access';
import { spreadIfSome } from '@ps/utils';

import { SurveyTagsService } from '../../services/survey-tags/survey-tags.service';
import { LocationUploadService } from '../../services/location/location-upload.service';
import { SurveyProcessingService } from '../../services/survey-processing/survey-processing.service';

import { SurveyCreationCoreApiActions } from '../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
} from './survey-creation-audience.actions';
import {
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
} from '../insights/survey-creation-insights.actions';
import {
  selectLinkedTagIds,
  selectLocationFileUpload,
  selectMarginForm,
  selectQualificationsForm,
  selectSelectedTagIds,
} from './survey-creation-audience.selectors';
import { selectSurveyId } from '../core/survey-creation-core.selectors';
import { InsightGate } from '../insights/insight-gate/insight-gate.service';
import { hasSurveyTagsChanged } from './utils/survey-tags-changes.helper';

@Injectable()
export class SurveyCreationAudienceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _insightGate = inject(InsightGate);

  private readonly _toastService = inject(ToastService);

  private readonly _surveyProcessingService = inject(SurveyProcessingService);
  private readonly _locationUploadService = inject(LocationUploadService);
  private readonly _surveyTagsService = inject(SurveyTagsService);

  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);
  private readonly _userSettingsActions = inject(UserSettingsActionsFacade);
  private readonly _tagsActions = inject(TagsActionsFacade);

  // The create page opens with no survey; an edit page's draft status is
  // known only once its survey loads, so edit keys off the root load (once per
  // survey) rather than its own init.
  public readonly loadTagsCatalog$ = createEffect(
    () =>
      merge(
        this._actions$.pipe(
          ofType(SurveyCreationAudienceActions.init),
          filter(({ surveyId }) => !surveyId),
          map((): Option<Survey> => Option.none),
        ),
        this._actions$.pipe(
          ofType(SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess),
          map(({ survey }): Option<Survey> => Option.some(survey)),
        ),
      ).pipe(
        this._insightGate.onlyIfAllowed(INSIGHT.TAGS, (survey) => survey),
        tap(() => this._tagsActions.loadTags()),
      ),
    { dispatch: false },
  );

  // The supplier slice is app-wide, so a new survey would inherit the last one's
  // allocations, and the CPIs on them still feed the navbar CPI. The create page
  // opens with no survey, so it starts from nothing; an edit page loads its own.
  public readonly resetSuppliersForNewSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.init),
      filter(({ surveyId }) => !surveyId),
      map(() => SupplierActions.resetAllocations()),
    ),
  );

  // A Buyer CPI the operator confirmed is the survey's offer price, so the
  // pricing follows it. Settling happens on blur (after any high-CPI
  // confirmation), never per keystroke, and an emptied field leaves the offer
  // price as it was.
  public readonly applyBuyerCpiAsOfferPrice$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.settleBuyerCpi),
      concatLatestFrom(() => this._store.select(selectMarginForm)),
      mergeMap(([, margin]) =>
        Option.from(margin.value?.buyerCpi)
          .filter((buyerCpi) => buyerCpi > 0)
          .match({
            some: (offerPrice) =>
              of(SurveyCreationInsightsActions.applyBuyerCpi({ offerPrice })),
            none: () => EMPTY,
          }),
      ),
    ),
  );

  // A managed-allocations Apply echoes the applied blend on its success action;
  // mirror it into the survey-creation state so the picker stays in sync. The
  // non-managed cpi-drawer Apply omits `blendId`, so it never touches state.
  public readonly syncSuppliersMetaDataBlendId$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.updateAllocationsSuccess),
      filter((action) => 'blendId' in action),
      map((action) =>
        SurveyCreationAudienceActions.setSuppliersMetaDataBlendId({
          blendId: action.blendId ?? '',
        }),
      ),
    ),
  );

  // The managed Apply persists the global price override, but the PUT response
  // never echoes it back — so mirror the applied pricing (carried on the success
  // action) into suppliersMetaData, the single source the panel reads to pick its
  // CPI mode. Non-managed Apply omits these keys, so it never touches state.
  public readonly syncSuppliersMetaDataPricing$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.updateAllocationsSuccess),
      filter((action) => 'priceOverride' in action),
      map((action) =>
        SurveyCreationAudienceActions.setSuppliersMetaData({
          metaData: {
            priceOverride: action.priceOverride ?? null,
            manualOverride: action.manualOverride ?? false,
            // Omitted on the action (not `null`) means the threshold wasn't
            // submitted because an override was active — leave the last known
            // value in place rather than nulling it, so turning the override
            // back off restores it instead of showing empty.
            ...spreadIfSome(action.surveyPauseThreshold, (value) => ({
              surveyPauseThreshold: value,
            })),
            defaultPurePriceOverride: action.defaultPurePriceOverride ?? false,
            isPurePriceRateCardEnable:
              action.isPurePriceRateCardEnable ?? false,
            supplierDefaultAllocation:
              action.supplierDefaultAllocation ?? false,
          },
        }),
      ),
    ),
  );

  // The managed Apply also sets the survey-wide global override; mirror the applied
  // offer price + pause threshold into the insights pricing slice so the active
  // child's offerPrice stays in step with suppliersMetaData.priceOverride. A
  // threshold that wasn't submitted is left out, keeping the saved one. The
  // cpi-drawer path already dispatches this via applyCpiPricing; this covers the
  // supplier-management-panel Apply, which does not.
  public readonly syncPricingGlobalOverride$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.updateAllocationsSuccess),
      filter((action) => 'priceOverride' in action),
      map((action) =>
        SurveyCreationInsightsApiActions.syncGlobalPriceOverride({
          offerPrice: action.priceOverride ?? null,
          ...spreadIfSome(action.surveyPauseThreshold, (value) => ({
            surveyPauseThreshold: value,
          })),
        }),
      ),
    ),
  );

  // Saving a blend creates it with a server id; mirror that id into the survey-
  // creation state so the picker selects the freshly saved blend from the single
  // metadata source rather than a separate saved-id slice.
  public readonly syncSavedBlendId$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.saveBlendSuccess),
      map(({ blend }) =>
        SurveyCreationAudienceActions.setSuppliersMetaDataBlendId({
          blendId: blend.id,
        }),
      ),
    ),
  );

  public readonly updatedSurveyQualificationsValidation$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationAudienceActions.updateQualifications),
        concatLatestFrom(() => [
          this._store.select(selectSurveyId),
          this._store.select(selectQualificationsForm),
        ]),
        filter(([, surveyId]) => !!surveyId),
        tap(([, , qualifications]) => {
          Option.from(qualifications.errors[0]?.message).inspect((message) =>
            this._toastService.error(message, { keepOpen: false }),
          );
        }),
      ),
    { dispatch: false },
  );

  public readonly uploadLocationCodes$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.uploadLocationCodes),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      mergeMap(
        ([
          { countryCode, qualificationCode, qualificationName, file },
          surveyId,
        ]) =>
          this._locationUploadService
            .uploadLocationCodes(
              countryCode,
              surveyId ?? 0,
              qualificationCode,
              file,
            )
            .pipe(
              tap((result) =>
                this._notifyLocationUpload(
                  result,
                  qualificationCode,
                  qualificationName,
                ),
              ),
              map((result) =>
                SurveyCreationAudienceApiActions.uploadLocationCodesSuccess({
                  qualificationCode,
                  qualificationName,
                  result,
                }),
              ),
              this.psCatchError(
                (error) =>
                  of(
                    SurveyCreationAudienceApiActions.uploadLocationCodesFailure(
                      {
                        qualificationCode,
                        error:
                          error.message ||
                          LOCATION_UPLOAD_MESSAGES.UPLOAD_FAILED,
                      },
                    ),
                  ),
                { notify: true },
              ),
            ),
      ),
    ),
  );

  public readonly downloadLocationCodesTemplate$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationAudienceActions.downloadLocationCodesTemplate),
        switchMap(({ countryCode, qualificationCode }) =>
          this._locationUploadService
            .downloadTemplateFile(countryCode, qualificationCode)
            .pipe(this.psCatchError(() => EMPTY, { notify: true })),
        ),
      ),
    { dispatch: false },
  );

  public readonly downloadValidatedLocationFile$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(SurveyCreationAudienceActions.downloadValidatedLocationFile),
        concatLatestFrom(() => this._store.select(selectLocationFileUpload)),
        map(([{ qualificationCode }, upload]) =>
          upload.qualificationCode === qualificationCode
            ? Option.from(upload.result)
            : Option.none,
        ),
        filter((upload) => upload.isSome()),
        switchMap((upload) => {
          const result = upload.unwrap();

          return this._locationUploadService
            .downloadValidatedFile(result.filePath, validCodeLabels(result))
            .pipe(this.psCatchError(() => EMPTY, { notify: true }));
        }),
      ),
    { dispatch: false },
  );

  public readonly loadSurveyTagsForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess),
      filter(({ survey }) => supportsSurveyTags(survey)),
      this._insightGate.onlyIfAllowed(INSIGHT.TAGS, ({ survey }) =>
        Option.some(survey),
      ),
      switchMap(({ survey }) =>
        this._surveyTagsService.getSurveyTags(survey.id).pipe(
          map((tagIds) =>
            SurveyCreationAudienceApiActions.loadSurveyTagsSuccess({
              tagIds,
            }),
          ),
          this.psCatchError((error) =>
            of(
              SurveyCreationAudienceApiActions.loadSurveyTagsFailure({
                error: error.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // Groups only exist for a file the buyer already uploaded, so only a survey that carries a live
  // upload qualification asks. switchMap drops an older request when a newer load lands; whether the
  // answer still applies is decided by the reducer, against the state it arrives to.
  public readonly loadLocationGroupsForEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess),
      switchMap(({ survey }) =>
        uploadedLocationQualification(survey).match({
          some: ({ qualificationCode }) =>
            this._locationUploadService
              .locationGroups(survey.id, qualificationCode)
              .pipe(
                map((groups) =>
                  SurveyCreationAudienceApiActions.loadLocationGroupsSuccess({
                    surveyId: survey.id,
                    qualificationCode,
                    filePath: locationFilePathFor(
                      survey,
                      qualificationCode,
                    ).unwrapOr(null),
                    groupedCodes: groupedCodesOf(groups),
                  }),
                ),
                this.psCatchError(() => EMPTY),
              ),
          none: () => EMPTY,
        }),
      ),
    ),
  );

  public readonly linkSurveyTags$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationAudienceApiActions.createStandardSurveySuccess,
        SurveyCreationCoreApiActions.saveSurveySuccess,
      ),
      filter(
        ({ survey }) =>
          !!this._userSettingsSelectors.buyerSettings().enableSurveyTags &&
          supportsSurveyTags(survey),
      ),
      concatLatestFrom(() => [
        this._store.select(selectSelectedTagIds),
        this._store.select(selectLinkedTagIds),
      ]),
      filter(([, selectedTagIds, linkedTagIds]) =>
        hasSurveyTagsChanged(selectedTagIds, linkedTagIds),
      ),
      map(([{ survey }, selectedTagIds]) => ({
        surveyId: survey.id,
        selectedTagIds,
      })),
      switchMap(({ surveyId, selectedTagIds }) =>
        this._surveyTagsService.linkSurveyTags(surveyId, selectedTagIds).pipe(
          map((tagIds) =>
            SurveyCreationAudienceApiActions.linkSurveyTagsSuccess({ tagIds }),
          ),
          this.psCatchError(
            (error) =>
              of(
                SurveyCreationAudienceApiActions.linkSurveyTagsFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly toggleManualProcessing$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.toggleManualProcessing),
      filter(() =>
        this._userSettingsSelectors.buyerConfigSettingsId().isSome(),
      ),
      switchMap(({ companyId, enabled }) => {
        const buyerConfigSettingsId = this._userSettingsSelectors
          .buyerConfigSettingsId()
          .unwrap();

        return this._surveyProcessingService
          .updateManualProcessing(buyerConfigSettingsId, companyId, enabled)
          .pipe(
            tap(() => {
              this._toastService.success(
                'Processing setting updated successfully',
              );
            }),
            tap(() => {
              this._userSettingsActions.updateBuyerSetting(
                'enableManualProcessing',
                companyId,
                enabled,
              );
            }),
            switchMap(() => {
              const logAction = SurveyActivityLogActions.logOperatorActivity({
                log: {
                  message: SURVEY_ACTIVITY_LOG_MESSAGES.manualProcessingToggled,
                  metadata: { action: enabled ? 'ON' : 'OFF' },
                },
              });

              return of(logAction);
            }),
            this.psCatchError(() => EMPTY, { notify: true }),
          );
      }),
    ),
  );

  private _notifyLocationUpload(
    result: SurveyLocationUploadResult,
    qualificationCode: number,
    qualificationName: string,
  ): void {
    if (!result.validCodes.length) {
      this._toastService.error(
        qualificationCode === QUALIFICATION_CODE.FSA
          ? LOCATION_UPLOAD_MESSAGES.INVALID_FSA_CODE_FILE
          : LOCATION_UPLOAD_MESSAGES.INVALID_LOCATION_CODE_FILE,
        { keepOpen: false },
      );
      return;
    }

    if (result.incorrectCodeCount) {
      this._toastService.warning(
        locationUploadRejectedMessage(
          result.incorrectCodeCount,
          result.incorrectCodes,
        ),
        { keepOpen: false },
      );
    }

    this._toastService.success(
      locationUploadSuccessMessage({
        correctCodeCount: result.correctCodeCount,
        totalCodeCount: result.totalCodeCount,
        duplicateCodeCount: result.duplicateCodeCount,
        modifiedCodeCount: result.modifiedCodeCount,
        qualificationName,
      }),
      { keepOpen: false },
    );
  }
}
